/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import {
    SubscriptionCtlProtoStartAddCreditCardReq, SubscriptionCtlProtoGetUpcomingInvoiceReq,
    SubscriptionCtlProtoListCreditCardsReq, SubscriptionCtlProtoListPreviousInvoicesReq,
    SubscriptionCtlProtoRemoveCreditCardReq, SubscriptionCtlProtoReq,
    SubscriptionCtlProtoSetDefaultCreditCardReq, SubscriptionCtlProtoGetAccessLevelsReq,
    SubscriptionCtlProtoSetGCPAccountReq, SubscriptionCtlProtoGetMarketplaceAccountsReq,
    SubscriptionCtlProtoGetMarketplaceAddressesReq, SubscriptionCtlProtoSetAzureAccountReq,
    SubscriptionCtlProtoListCreditsReq, SubscriptionCtlProtoListDiscountsReq, SubscriptionCtlProtoSetAWSAccountReq
} from "../../../application/protocol/subscription-controller";
import { ApiListResponse, ApiResponse, responseListOf, responseOf } from "../../concept/api-response";
import { Parameters, parametersProtoOf } from "../../concept/common";
import { ok, Ok } from "../../concept/base";
import { AccessLevel, accessLevelOf } from "../../concept/iam";
import {
    AddCreditCardResponse,
    CreditCard, creditCardOf,
    Credits, creditsOf,
    Discount, discountOf,
    MarketplaceAddresses,
    GCPAccount, gcpAccountOf,
    AzureAccount, azureAccountOf,
    AWSAccount, awsAccountOf,
    Invoice, partialInvoiceOf,
} from "../../concept/subscription";
import { stringToBytes } from "../../util";
import { ApiService } from "../backend/api.service";
import { UnsubListener } from "../backend/cloud-api-backend.service";

@Injectable({
    providedIn: "root",
})
export class SubscriptionApi {
    constructor(private api: ApiService) {}

    getAccessLevels(teamUuids: string[]): Observable<AccessLevel[]> {
        return this.api.subscriptionReqRes(new SubscriptionCtlProtoReq({ getAccessLevels: new SubscriptionCtlProtoGetAccessLevelsReq({ teamUuids: teamUuids.map(stringToBytes) }) }))
            .pipe(map((res) => res.getAccessLevels.accessLevels.map(accessLevelOf)));
    }

    startAddCreditCard(teamUuid: string): Observable<AddCreditCardResponse> {
        return this.api.subscriptionReqRes(new SubscriptionCtlProtoReq({
            startAddCreditCard: new SubscriptionCtlProtoStartAddCreditCardReq({ teamUuid: stringToBytes(teamUuid) })
        })).pipe(map((res) => ({ token: res.startAddCreditCard.token, apiKey: res.startAddCreditCard.apiKey })));
    }

    listCreditCards(teamUuid: string): Observable<CreditCard[]> {
        return this.api.subscriptionReqRes(new SubscriptionCtlProtoReq({
            listCreditCards: new SubscriptionCtlProtoListCreditCardsReq({
                teamUuid: stringToBytes(teamUuid)
            })
        })).pipe(map((res) => res.listCreditCards.creditCards.map(creditCardOf)));
    }

    removeCreditCard(teamUuid: string, creditCardId: string): Observable<Ok> {
        return this.api.subscriptionReq(new SubscriptionCtlProtoReq({
            removeCreditCard: new SubscriptionCtlProtoRemoveCreditCardReq({
                teamUuid: stringToBytes(teamUuid),
                creditCardId,
            })
        }));
    }

    setDefaultCreditCard(teamUuid: string, creditCardId: string): Observable<Ok> {
        return this.api.subscriptionReq(new SubscriptionCtlProtoReq({
            setDefaultCreditCard: new SubscriptionCtlProtoSetDefaultCreditCardReq({
                teamUuid: stringToBytes(teamUuid),
                creditCardId,
            })
        }));
    }

    setGCPAccount(teamUuid: string, jwt: string): Observable<Ok> {
        return this.api.subscriptionReq(new SubscriptionCtlProtoReq({
            setGcpAccount: new SubscriptionCtlProtoSetGCPAccountReq({
                teamUuid: stringToBytes(teamUuid),
                jwt
            })
        }));
    }

    setAzureAccount(teamUuid: string, token: string): Observable<Ok> {
        return this.api.subscriptionReq(new SubscriptionCtlProtoReq({
            setAzureAccount: new SubscriptionCtlProtoSetAzureAccountReq({
                teamUuid: stringToBytes(teamUuid),
                token
            })
        }));
    }

    setAWSAccount(teamUuid: string, token: string): Observable<Ok> {
        return this.api.subscriptionReq(new SubscriptionCtlProtoReq({
            setAwsAccount: new SubscriptionCtlProtoSetAWSAccountReq({
                teamUuid: stringToBytes(teamUuid),
                token
            })
        }));
    }

    getMarketplaceAccounts(teamUuid: string): Observable<{ gcp: GCPAccount | null, azure: AzureAccount | null, aws: AWSAccount | null }> {
        return this.api.subscriptionReqRes(new SubscriptionCtlProtoReq({
            getMarketplaceAccounts: new SubscriptionCtlProtoGetMarketplaceAccountsReq({
                teamUuid: stringToBytes(teamUuid)
            })
        })).pipe(map((res) => {
            return {
                gcp: res.getMarketplaceAccounts.hasGcpAccount ? gcpAccountOf(res.getMarketplaceAccounts.gcpAccount) : null,
                azure: res.getMarketplaceAccounts.hasAzureAccount ? azureAccountOf(res.getMarketplaceAccounts.azureAccount) : null,
                aws: res.getMarketplaceAccounts.hasAwsAccount ? awsAccountOf(res.getMarketplaceAccounts.awsAccount) : null,
            }
        }))
    }

    listCredits(teamUuid: string, params: Parameters, unsub$: UnsubListener): Observable<ApiListResponse<Credits>> {
        return this.api.subscriptionReqSub(new SubscriptionCtlProtoReq({
            listCredits: new SubscriptionCtlProtoListCreditsReq({
                teamUuid: stringToBytes(teamUuid),
                params: parametersProtoOf(params)
            })
        }), unsub$).pipe(map((res) => responseListOf(res.type, res.data.listCredits.credits.map(creditsOf))));
    }

    listDiscounts(teamUuid: string, params: Parameters, unsub$: UnsubListener): Observable<ApiListResponse<Discount>> {
        return this.api.subscriptionReqSub(new SubscriptionCtlProtoReq({
            listDiscounts: new SubscriptionCtlProtoListDiscountsReq({
                teamUuid: stringToBytes(teamUuid),
                params: parametersProtoOf(params)
            })
        }), unsub$).pipe(map((res) => responseListOf(res.type, res.data.listDiscounts.discounts.map(discountOf))));
    }

    getUpcomingInvoice(teamUuid: string, unsub$: UnsubListener): Observable<ApiResponse<Invoice> | null> {
        return this.api.subscriptionReqSub(new SubscriptionCtlProtoReq({
            getUpcomingInvoice: new SubscriptionCtlProtoGetUpcomingInvoiceReq({
                teamUuid: stringToBytes(teamUuid)
            })
        }), unsub$).pipe(map((res) => {
            return res.data.getUpcomingInvoice.hasInvoice
                ? responseOf(res.type, partialInvoiceOf(res.data.getUpcomingInvoice.invoice))
                : null;
        }));
    }

    listPreviousInvoices(teamUuid: string, params: Parameters, unsub$: UnsubListener): Observable<ApiListResponse<Invoice>> {
        return this.api.subscriptionReqSub(new SubscriptionCtlProtoReq({
            listPreviousInvoices: new SubscriptionCtlProtoListPreviousInvoicesReq({
                teamUuid: stringToBytes(teamUuid),
                params: parametersProtoOf(params)
            })
        }), unsub$)
            .pipe(map((res) => responseListOf(res.type, res.data.listPreviousInvoices.invoices.map(partialInvoiceOf))));
    }

    getMarketplaceAddresses(): Observable<MarketplaceAddresses> {
        return this.api.subscriptionReqRes(new SubscriptionCtlProtoReq({
            getMarketplaceAddresses: new SubscriptionCtlProtoGetMarketplaceAddressesReq({})
        })).pipe(map((res) => res.getMarketplaceAddresses))
    }
}
